/**
 * 기사앱 PWA — 설치·푸시 알림 헬퍼(2026-09-27, DYOPS·YDYOPS lib/notifications.ts + InstallPrompt 미러).
 *
 * - 서비스 워커는 `/driver-sw.js`(scope `/driver/`) 하나. 설치 조건(fetch handler)과 백그라운드 푸시를 같이 맡는다.
 * - `beforeinstallprompt` 는 **모듈 로드 즉시** 잡아 둔다 — 화면이 뜨기 전에 쏘는 일이 있다.
 *   stash 는 이 모듈 하나에만(두 곳에서 잡으면 먼저 잡은 쪽만 받는다 — DYOPS 실측).
 * - FCM 토큰은 VAPID 키 없이 받는다(Firebase 기본 키). 콘솔에서 웹 푸시 인증서를 만들 필요가 없다.
 *
 * 기기별 제약(화면 문구가 이걸 따른다):
 *  - 카카오톡 안 브라우저: 설치·알림 둘 다 안 된다 → 바깥 브라우저로 열게 한다.
 *  - 플레이 스토어 앱(native/driver-android): 설치 안내 없음 · 알림은 앱 플러그인(FCM 네이티브 토큰).
 *  - 앱스토어 앱(native/driver-ios): 같음 · 알림 플러그인만 `FirebaseMessaging`(APNs → FCM 토큰).
 *  - iPhone: 설치는 Safari 「공유 → 홈 화면에 추가」 수동뿐. 알림은 **홈 화면 앱으로 들어왔을 때만**(iOS 16.4+).
 *    홈 화면 앱은 Safari 와 저장소가 따로라 **한 번 다시 로그인**해야 한다.
 */

import { getMessaging, getToken, isSupported } from 'firebase/messaging';
import { app } from './firebase';
import { driverApi } from './driverApi';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let stashed: BeforeInstallPromptEvent | null = null;
const INSTALLED_KEY = 'businweb.driver.installed';

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    stashed = e as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event('driver:installable'));
  });
  window.addEventListener('appinstalled', () => {
    stashed = null;
    try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* 저장 못 해도 안내가 한 번 더 뜰 뿐 */ }
    window.dispatchEvent(new Event('driver:installed'));
  });
}

export function hasInstallPrompt(): boolean {
  return !!stashed;
}

/** 크롬 설치 창을 띄운다. 잡아 둔 이벤트가 없으면 'unavailable'(화면이 메뉴 안내로 바꾼다). */
export async function triggerInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!stashed) return 'unavailable';
  const e = stashed;
  stashed = null;
  await e.prompt();
  return (await e.userChoice).outcome;
}

/**
 * 플레이 스토어 앱(2026-10-07 · `native/driver-android`, Capacitor 원격 모드)으로 열렸는가.
 * 앱이 UA 끝에 `BusinDriverApp/1` 을 붙인다(브리지가 뜨기 전에도 안다) · 브리지가 있으면 그것도 본다.
 * 🔴 앱 화면은 안드로이드 WebView 라 UA 에 `; wv)` 가 있다 → 이걸 먼저 보지 않으면 「이 화면에서는 앱 설치가 안 됩니다」가 뜬다.
 */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false;
  return /BusinDriverApp\//.test(navigator.userAgent) || !!nativeBridge()?.isNativePlatform?.();
}

type PushPlugin = {
  checkPermissions: () => Promise<{ receive: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' }>;
  requestPermissions: () => Promise<{ receive: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' }>;
  register: () => Promise<void>;
  createChannel: (c: { id: string; name: string; description?: string; importance?: number; visibility?: number }) => Promise<void>;
  addListener: (ev: string, fn: (x: any) => void) => Promise<{ remove: () => Promise<void> }>;
};
/**
 * 아이폰 앱(native/driver-ios · 2026-10-12)은 `@capacitor-firebase/messaging` 을 쓴다 — `PushNotifications` 는 iOS 에서
 * APNs 토큰을 주는데 서버(FCM)는 FCM 토큰이 필요하다. 안드로이드 앱은 `PushNotifications` 그대로(이미 깔린 1.1 이 그것).
 * 🔴 분기는 OS 가 아니라 **플러그인이 있나**로 — 원격 모드라 웹은 매일 바뀌고 앱 빌드마다 플러그인이 다르다.
 */
type FcmPlugin = {
  checkPermissions: () => Promise<{ receive: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' }>;
  requestPermissions: () => Promise<{ receive: 'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' }>;
  getToken: () => Promise<{ token: string }>;
  addListener: (ev: string, fn: (x: any) => void) => Promise<{ remove: () => Promise<void> }>;
};
type Bridge = { isNativePlatform?: () => boolean; Plugins?: { PushNotifications?: PushPlugin; FirebaseMessaging?: FcmPlugin } };
function nativeBridge(): Bridge | undefined {
  return (window as unknown as { Capacitor?: Bridge }).Capacitor;
}
const nativePush = (): PushPlugin | undefined => nativeBridge()?.Plugins?.PushNotifications;
const nativeFcm = (): FcmPlugin | undefined => nativeBridge()?.Plugins?.FirebaseMessaging;

// 앱에서 알림을 누르면 그 공지로(서버가 data.url 을 넣는다). 모듈 로드 때 한 번.
if (typeof window !== 'undefined' && isNativeApp()) {
  const go = (a: { notification?: { data?: { url?: string } } }) => {
    const url = a?.notification?.data?.url;
    if (url && url.startsWith('/')) location.assign(url);
  };
  const fcm = nativeFcm();
  if (fcm) void fcm.addListener('notificationActionPerformed', go);
  else void nativePush()?.addListener('pushNotificationActionPerformed', go);
}

/** 홈 화면 앱으로 실행 중인가(스토어 앱 포함 — 설치 안내가 필요 없다). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (isNativeApp()) return true;
  return window.matchMedia?.('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && 'ontouchend' in document);
}

/**
 * 다른 앱 안의 웹 화면(인앱)인가 — 구 버스인 기사앱 공지 링크(2026-10-07 way)·카카오톡·네이버·밴드 등.
 * 안드로이드 = UA 의 `; wv)`(시스템 WebView 표식) · 아이폰 = `Safari/` 토큰이 없다(사파리·크롬·파이어폭스엔 있다).
 * 🔴 홈 화면에 설치한 앱(standalone)도 아이폰은 `Safari/` 가 없다 → 설치 앱은 인앱이 아니다.
 */
export function isInAppBrowser(): boolean {
  if (isStandalone()) return false;
  const ua = navigator.userAgent;
  if (/KAKAOTALK|NAVER\(inapp|DaumApps|BAND\/|Line\/|Instagram|FBAN|FBAV/i.test(ua)) return true;
  if (/android/i.test(ua)) return /; wv\)/.test(ua);
  return isIos() && !/Safari\//.test(ua);
}

export function isKakaoInApp(): boolean {
  return /KAKAOTALK/i.test(navigator.userAgent);
}

/**
 * 카카오톡 → 폰 기본 브라우저로 같은 주소를 연다(카카오 공식 스킴).
 * 🔴 다른 앱(구 버스인 기사앱)엔 쓰지 않는다 — intent:// 를 못 받아 「웹페이지를 사용할 수 없음」이 떴다(10-07 실기기). 그쪽은 주소 복사.
 */
export function openInExternalBrowser(): void {
  location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(location.href)}`;
}

/**
 * 주소를 클립보드에 — 오래된 WebView 는 `navigator.clipboard` 가 없거나 막혀 있어 textarea + execCommand 로 한 번 더.
 * 성공하면 true.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* 아래로 */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * 이 폰에 이미 설치했는가 — 브라우저로 들어왔어도 알 수 있으면 안내를 멈춘다.
 * 안드로이드 크롬은 manifest `related_applications`(webapp) 로 `getInstalledRelatedApps` 가 답한다.
 * 모르면 false(계속 안내 — way: 「설정 전까지 계속」).
 */
export async function isInstalledElsewhere(): Promise<boolean> {
  try {
    if (localStorage.getItem(INSTALLED_KEY) === '1') return true;
  } catch { /* 무시 */ }
  const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> };
  if (!nav.getInstalledRelatedApps) return false;
  try {
    return (await nav.getInstalledRelatedApps()).length > 0;
  } catch {
    return false;
  }
}

let swReg: Promise<ServiceWorkerRegistration | null> | null = null;

/** 서비스 워커 등록 — 기사앱이 뜰 때 한 번. 크롬 설치 조건이라 알림과 무관하게 먼저 건다. */
export function registerDriverSw(): Promise<ServiceWorkerRegistration | null> {
  if (!swReg) {
    swReg = 'serviceWorker' in navigator
      ? navigator.serviceWorker.register('/driver-sw.js', { scope: '/driver/' }).catch((e) => {
          console.warn('[driver-sw] 등록 실패', e);
          return null;
        })
      : Promise.resolve(null);
  }
  return swReg;
}

export type PushState = 'unsupported' | 'default' | 'granted' | 'denied';

export function pushPermission(): PushState {
  if (isNativeApp()) return 'unsupported'; // 앱은 비동기로만 안다 → nativePushPermission()
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  return Notification.permission as PushState;
}

/** 스토어 앱의 알림 권한(앱이 아니면 웹 값). */
export async function pushPermissionAsync(): Promise<PushState> {
  if (!isNativeApp()) return pushPermission();
  const p = nativeFcm() ?? nativePush();
  if (!p) return 'unsupported';
  try {
    const r = (await p.checkPermissions()).receive;
    return r === 'granted' ? 'granted' : r === 'denied' ? 'denied' : 'default';
  } catch {
    return 'unsupported';
  }
}

/** 스토어 앱 — 권한 → 채널 → 등록 → 토큰을 서버에. 토큰 user_agent 앞에 `BusinDriverApp` 을 붙여 서버가 앱 형식으로 보낸다. */
async function enableNativePush(ask: boolean): Promise<{ ok: true } | { ok: false; reason: string }> {
  const fcm = nativeFcm();
  if (fcm) {
    // 아이폰 앱 — 권한 → FCM 토큰(플러그인이 APNs 토큰을 FCM 토큰으로 바꿔 준다). 채널은 안드로이드 전용이라 없다.
    try {
      const r = (ask ? await fcm.requestPermissions() : await fcm.checkPermissions()).receive;
      if (r !== 'granted') return { ok: false, reason: '알림이 허용되지 않았습니다.' };
      const { token } = await fcm.getToken();
      if (!token) return { ok: false, reason: '알림 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
      await driverApi.registerPush({ token, userAgent: `BusinDriverApp ${navigator.userAgent}`.slice(0, 300) });
      return { ok: true };
    } catch (e) {
      console.warn('[driver-push] 앱 토큰 등록 실패', e);
      return { ok: false, reason: '알림 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
    }
  }
  const p = nativePush();
  if (!p) return { ok: false, reason: '앱을 최신으로 업데이트해 주세요.' };
  try {
    const r = (ask ? await p.requestPermissions() : await p.checkPermissions()).receive;
    if (r !== 'granted') return { ok: false, reason: '알림이 허용되지 않았습니다.' };
    await p.createChannel({ id: 'notice', name: '공지·배차 알림', importance: 5, visibility: 1 });
    // 앱을 열 때마다 부르므로 리스너는 쓰고 바로 뗀다(쌓이면 토큰 하나에 등록이 여러 번 간다).
    // 🔴 원격 모드 브리지의 addListener 는 Promise 가 아니라 {remove} 를 그대로 준다(@capacitor/core 를 안 싣는다) —
    //    `.then` 을 바로 부르면 토큰을 받은 뒤 finally 에서 터져 서버 등록이 안 됐다(안드로이드 앱 토큰 역대 0건 · 10-25).
    const subs: (Promise<{ remove: () => Promise<void> }> | { remove: () => Promise<void> })[] = [];
    const token = await new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), 15000);
      subs.push(p.addListener('registration', (t: { value: string }) => { clearTimeout(timer); resolve(t.value); }));
      subs.push(p.addListener('registrationError', (e: unknown) => { clearTimeout(timer); reject(e); }));
      void p.register();
    }).finally(() => subs.forEach((s) => void Promise.resolve(s).then((h) => h.remove()).catch(() => {})));
    await driverApi.registerPush({ token, userAgent: `BusinDriverApp ${navigator.userAgent}`.slice(0, 300) });
    return { ok: true };
  } catch (e) {
    console.warn('[driver-push] 앱 토큰 등록 실패', e);
    return { ok: false, reason: '알림 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
  }
}

/**
 * 알림 켜기 — 권한 요청 → 토큰 → 서버 등록. `ask=false` 면 권한 창은 띄우지 않고
 * 이미 허용된 폰의 토큰만 다시 올린다(앱을 열 때마다 조용히 — 토큰이 바뀌어도 따라간다).
 */
export async function enableDriverPush(ask = true): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (isNativeApp()) return enableNativePush(ask);
  if (!(await isSupported().catch(() => false))) {
    return { ok: false, reason: '이 브라우저는 알림을 받을 수 없습니다.' };
  }
  if (isIos() && !isStandalone()) {
    return { ok: false, reason: '아이폰은 홈 화면에 추가한 앱으로 들어와야 알림을 켤 수 있습니다.' };
  }
  const permission = ask ? await Notification.requestPermission() : Notification.permission;
  if (permission !== 'granted') return { ok: false, reason: '알림이 허용되지 않았습니다.' };
  const reg = await registerDriverSw();
  if (!reg) return { ok: false, reason: '알림 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
  try {
    await navigator.serviceWorker.ready;
    const token = await getToken(getMessaging(app), { serviceWorkerRegistration: reg });
    if (!token) return { ok: false, reason: '알림 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
    await driverApi.registerPush({ token, userAgent: navigator.userAgent.slice(0, 300) });
    return { ok: true };
  } catch (e) {
    console.warn('[driver-push] 토큰 등록 실패', e);
    return { ok: false, reason: '알림 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
  }
}
